import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const SolarDocumentsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    competencia_mes: '',
    competencia_ano: '',
    status: 'TODOS',
    search: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/solar/documents');
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const openPdf = async (chargeId, fileName) => {
    try {
      const response = await axios.get(`/api/solar/documents/${chargeId}/pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.download = fileName || `solar-${chargeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao abrir PDF');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredItems = useMemo(() => {
    const mes = Number(filters.competencia_mes);
    const ano = Number(filters.competencia_ano);
    const hasMes = Number.isInteger(mes) && mes >= 1 && mes <= 12;
    const hasAno = Number.isInteger(ano) && ano >= 2000 && ano <= 2100;
    const search = String(filters.search || '').trim().toLowerCase();

    return items.filter((item) => {
      if (filters.status !== 'TODOS' && item.charge_status !== filters.status) return false;
      if (hasMes && Number(item.competencia_mes) !== mes) return false;
      if (hasAno && Number(item.competencia_ano) !== ano) return false;
      if (!search) return true;

      const haystack = [item.apelido, item.numero_instalacao_enel, item.customer_nome, item.charge_id, item.file_name]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(search);
    });
  }, [filters.competencia_ano, filters.competencia_mes, filters.search, filters.status, items]);

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar - Documentos</h1>
          <p className="text-gray-600">PDFs de cobrancas e ajustes gerados automaticamente a partir das faturas ENEL</p>
        </div>
        {loading && <div className="loader h-6 w-6" />}
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-6 gap-3 border-b">
          <div>
            <label className="form-label">Competencia Mes</label>
            <input
              className="form-input"
              type="number"
              min="1"
              max="12"
              placeholder="MM"
              value={filters.competencia_mes}
              onChange={(e) => setFilters((old) => ({ ...old, competencia_mes: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Competencia Ano</label>
            <input
              className="form-input"
              type="number"
              placeholder="AAAA"
              value={filters.competencia_ano}
              onChange={(e) => setFilters((old) => ({ ...old, competencia_ano: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Status Cobranca</label>
            <select className="form-select" value={filters.status} onChange={(e) => setFilters((old) => ({ ...old, status: e.target.value }))}>
              <option value="TODOS">Todos</option>
              <option value="EMITIDA">EMITIDA</option>
              <option value="ENVIADA">ENVIADA</option>
              <option value="VENCIDA">VENCIDA</option>
              <option value="PAGA">PAGA</option>
              <option value="PARCIAL">PARCIAL</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="form-label">Buscar</label>
            <input
              className="form-input"
              placeholder="UC, cliente, arquivo ou cobranca"
              value={filters.search}
              onChange={(e) => setFilters((old) => ({ ...old, search: e.target.value }))}
            />
          </div>
          <div className="flex items-end">
            <button
              className="btn-secondary w-full"
              onClick={() => setFilters({ competencia_mes: '', competencia_ano: '', status: 'TODOS', search: '' })}
            >
              Limpar
            </button>
          </div>
        </div>
        <div className="card-header">Documentos ({filteredItems.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Competencia</th>
                  <th>UC</th>
                  <th>Cliente</th>
                  <th>Cobranca</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {String(item.competencia_mes || '').padStart(2, '0')}/{item.competencia_ano || '-'}
                    </td>
                    <td>{item.apelido || item.numero_instalacao_enel || '-'}</td>
                    <td>{item.customer_nome}</td>
                    <td>{item.charge_id?.slice(0, 8)}</td>
                    <td>R$ {Number(item.valor_total || item.valor_original || 0).toFixed(2)}</td>
                    <td>{item.charge_status || '-'}</td>
                    <td>
                      <button
                        className="text-primary-600 hover:text-primary-900 font-medium inline-flex items-center gap-1"
                        onClick={() => openPdf(item.charge_id, item.file_name)}
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      Nenhum documento encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolarDocumentsPage;
